import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faCirclePlus } from "@fortawesome/pro-regular-svg-icons";

/**
 * PBI #299: an option and, when the caller supplied one, its description on a muted second
 * line. Options themselves stay plain strings, so a missing or blank description simply
 * renders nothing — never the text "null" or "undefined".
 */
const OptionLabel = ({ option, description }) => (
  <span className="block">
    <span className="block">{option}</span>
    {description ? (
      <span className="block text-xs font-normal text-gray-500">{description}</span>
    ) : null}
  </span>
);

const Dropdown = ({
  label,
  size = "default",
  options = [],
  multiSelect = false,
  searchable = false,
  withAddButton = false,
  className = "",
  selectedOptions = [],
  isActie,
  disabled = false,
  value = "",
  setIsActive = () => {},
  setSelectedOptions = () => {},
  onSelect = () => {},
  managedSelections = false,
  // PBI #299: optional map from option string to a description, rendered as a muted second
  // line under the option and under the selected value. Omitted by every existing caller.
  descriptions = null,
}) => {
  const [confirmedSelections, setConfirmedSelections] = useState(
    !multiSelect && selectedOptions ? selectedOptions : []
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(isActie ? true : false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const inputClass = size === "small" ? "input-default-sm" : "input-default";

  const safeSelected = Array.isArray(selectedOptions)
  ? selectedOptions
  : typeof selectedOptions === "string"
  ? [selectedOptions]
  : [];

  /**
   * PBI #299: `descriptions` maps an option string to its description. Anything missing,
   * null or blank yields no description line at all.
   */
  const descriptionFor = (option) => {
    const description = descriptions ? descriptions[option] : null;
    return typeof description === "string" && description.trim() !== ""
      ? description
      : null;
  };

  const selectedDescription = descriptionFor(safeSelected[0] || value || "");

/*   const displayValue = () => {
    if (searchable && isOpen) return searchQuery;

    if (multiSelect && safeSelected.length > 0) {
      return safeSelected.join(", ");
    }

    return safeSelected[0] || value || "";
  } */
  
    const displayValue = () => {
      if (searchable && isOpen) return searchQuery;
      if (multiSelect && safeSelected.length > 0) return safeSelected.join(", ");
      return safeSelected[0] || value || "";
    };

  useEffect(() => {
    if (!multiSelect && selectedOptions && managedSelections) {
      setConfirmedSelections(selectedOptions);
    }
  }, [selectedOptions]);

  useEffect(() => {
    setConfirmedSelections(value);
  }, [value]);

  const handleOptionChange = (option) => {
    if (disabled) return; // Prevent selection if disabled
    if (multiSelect) {
      const newSelection = selectedOptions.includes(option)
        ? selectedOptions.filter((item) => item !== option)
        : [...selectedOptions, option];
      setSelectedOptions(newSelection);
      if (!withAddButton) {
        setConfirmedSelections(newSelection);
        onSelect(newSelection);
        setSearchQuery("");
      }
    } else {
      setSelectedOptions([option]);
      setConfirmedSelections([option]);
      onSelect(option);
      setIsOpen(false);
      setSearchQuery("");
    }
  };

  const handleAddClick = () => {
    setConfirmedSelections(selectedOptions);
    onSelect(selectedOptions);
    setSearchQuery("");
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    if (!isOpen || !disabled) {
      !isActie && setIsActive(label);
    }
    setIsOpen(!isOpen);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setIsOpen(true);
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const filteredOptions = searchable
  ? options.filter(
      (option) =>
        typeof option === "string" &&
        option.toLowerCase().includes(searchQuery.toLowerCase())
    )
  : options;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!isOpen) return;
      if (
        (dropdownRef.current && !dropdownRef.current.contains(event.target)) ||
        (buttonRef.current && !buttonRef.current.contains(event.target))
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div ref={dropdownRef} className={`flex flex-col w-full ${className}`}>
      <label className="block text-sm font-bold text-gray-800 mb-2">
        {label}
      </label>
      <div className="relative">

        {(!isOpen || !searchable) && 
        <button
          onClick={!disabled ? toggleDropdown : undefined}
          disabled={disabled}
          className={`w-full text-left text-sm bold whitespace-nowrap overflow-hidden text-ellipsis px-2 py-2 border mr-4 rounded-md ${
            disabled
              ? "bg-gray-100 cursor-not-allowed opacity-60 border-gray-300" // Matches disabled InputField
              : "input-default"
          }`}
        >
          <div className="flex whitespace-nowrap">
            {displayValue() || "Select"}
          </div>
        </button>}
        
        {(isOpen && searchable) && 
         <input
         type="text"
         value={searchQuery}
         onChange={handleSearchChange}
         placeholder="Search..."
         className="input-default w-full"
       />}

        <FontAwesomeIcon
          icon={faChevronDown}
          className={`absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none ${
            disabled ? "opacity-60" : ""
          }`}
        />

        {!disabled && isOpen && (
          <div className="absolute left-0 w-full bg-white border rounded-md shadow-lg z-10">
            {withAddButton && (
              <div className="p-4 ">
                <button
                  onClick={handleAddClick}
                  className="rounded-md w-full top-0 px-4 py-2 bg-primary-300 text-primary-700 font-semibold flex flex-row gap-2 items-center justify-center hover:bg-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50"
                >
                        <FontAwesomeIcon
                  icon={faCirclePlus}
                  className=""
                />
                  <span>Add selected</span>
                </button>
                </div>
            )}
            <div className="max-h-80 overflow-y-auto">
            {Array.isArray(filteredOptions) && filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <label
                  key={index}
                  className="flex items-center py-2 hover:bg-blue-50 space-x-[16px]"
                >
                  {multiSelect ? (
                    <>
                      <input
                        type="checkbox"
                        className="custom-checkbox-input hidden"
                        checked={selectedOptions.includes(option)}
                        onChange={() => handleOptionChange(option)}
                      />
                      <div className="custom-checkbox">
                        <svg
                          className="custom-checkbox-icon"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M5 13l4 4L19 7"
                          ></path>
                        </svg>
                      </div>
                      <OptionLabel
                        option={option}
                        description={descriptionFor(option)}
                      />
                    </>
                  ) : (
                    <button
                      onClick={() => handleOptionChange(option)}
                      className="block w-full text-left mx-4"
                    >
                      <OptionLabel
                        option={option}
                        description={descriptionFor(option)}
                      />
                    </button>
                  )}
                </label>
              ))
            ) : (
              <p className="text-black px-4 py-2">No options available</p>
            )}
            </div>
          </div>
        )}
      </div>

      {/* PBI #299: the selected option's description, so the choice can be understood
          without opening the list. */}
      {selectedDescription && (
        <p className="text-sm text-gray-500 mt-1">{selectedDescription}</p>
      )}
    </div>
  );
};

export default Dropdown;
